// Mirrors server/utils/shippingCalc.js — keeps cart/checkout preview in sync
// with what the server will actually charge.
// Settings come from the public GET /api/shipping-settings endpoint.

function norm(s) { return String(s || '').trim().toLowerCase(); }

export function pickCheapestRate(rates) {
  const enabled = (rates || []).filter(r => r.enabled !== false);
  if (enabled.length === 0) return null;
  return enabled.reduce((min, r) => (r.price < min.price ? r : min), enabled[0]);
}

export function matchZone(zones, state) {
  if (!state) return null;
  const ns = norm(state);
  return (zones || []).find(
    z => z.enabled !== false && (z.regions || []).some(r => norm(r) === ns)
  ) || null;
}

/**
 * Returns the matching local-delivery city entry (or null).
 * Priority: pincode match > state+city match > city-only match.
 */
export function matchLocalDelivery(settings, { city, state, pincode } = {}) {
  if (!settings?.localDeliveryEnabled) return null;
  if (!city && !pincode) return null;

  const nc = city ? norm(city) : null;
  const ns = state ? norm(state) : null;
  const np = pincode ? norm(pincode) : null;

  const allEnabled = (settings.localDeliveryCities || []).filter(c => c.enabled !== false);

  // 1. Pincode-first
  if (np) {
    const pincodeMatch = allEnabled.find(
      c => (c.pincodes || []).length > 0 && c.pincodes.some(p => norm(p) === np)
    );
    if (pincodeMatch) return pincodeMatch;
  }

  // 2. City/state match (only entries with no pincode restriction)
  if (!nc) return null;
  const cityEligible = allEnabled.filter(c => !c.pincodes || c.pincodes.length === 0);
  const candidates = cityEligible.filter(c => norm(c.city) === nc);
  if (candidates.length === 0) return null;

  if (ns) {
    const stateMatch = candidates.find(c => c.state && norm(c.state) === ns);
    if (stateMatch) return stateMatch;
  }
  return candidates[0];
}

/**
 * Returns { minDays, maxDays, label } from delivery-estimate rules, or null.
 * City-level takes precedence over state-level.
 */
export function getDeliveryEstimate(settings, { state, city } = {}) {
  if (!settings?.deliveryEstimatesEnabled) return null;
  if (!state) return null;

  const ns = norm(state);
  const nc = city ? norm(city) : null;
  const enabled = (settings.deliveryEstimates || []).filter(e => e.enabled !== false);

  if (nc) {
    const cityRule = enabled.find(
      e => norm(e.state) === ns &&
           (e.cities || []).length > 0 &&
           e.cities.some(c => norm(c) === nc)
    );
    if (cityRule) return { minDays: cityRule.minDays, maxDays: cityRule.maxDays, label: cityRule.label };
  }

  const stateRule = enabled.find(
    e => norm(e.state) === ns && (!e.cities || e.cities.length === 0)
  );
  if (stateRule) return { minDays: stateRule.minDays, maxDays: stateRule.maxDays, label: stateRule.label };

  return null;
}

/**
 * Returns true if COD is available for this state / city / pincode.
 */
export function isCodAvailable(settings, { state, city, pincode } = {}) {
  if (!settings?.codEnabled) return false;
  if (!settings.codRegionsEnabled) return true;

  const ns = norm(state);
  const nc = norm(city);
  const np = pincode ? norm(pincode) : null;

  const match = (settings.codRegions || []).find(
    r => r.enabled !== false && norm(r.state) === ns
  );
  if (!match) return false;

  if (match.pincodes && match.pincodes.length > 0) {
    return np ? match.pincodes.some(p => norm(p) === np) : false;
  }
  if (match.cities && match.cities.length > 0) {
    return match.cities.some(c => norm(c) === nc);
  }
  return true;
}

/**
 * Compute shipping for the client-side preview.
 *
 * @param {object|null} settings  result of getShippingSettings(), or null while loading
 * @param {{ subtotal: number, state?: string, city?: string, pincode?: string }} order
 * @returns {{ amount, label, minDays, maxDays, isLocalDelivery, codAvailable }}
 */
export function computeShipping(settings, { subtotal, state, city, pincode } = {}) {
  if (!settings) {
    // Settings still loading — return null so UI shows a pending state
    return {
      amount: null,
      label: 'Calculating…',
      minDays: null, maxDays: null,
      isLocalDelivery: false,
      isFreeShipping: false,
      codAvailable: true,
      loading: true,
    };
  }

  const codAvailable = isCodAvailable(settings, { state, city, pincode });

  // Whether this order qualifies for free shipping — applied as a price override
  // on whichever method matches, so the label/description stay unchanged.
  const isFreeShipping = !!(settings.freeShippingEnabled && subtotal >= settings.freeShippingThreshold);

  // 1. Local delivery — highest priority
  const localCity = matchLocalDelivery(settings, { city, state, pincode });
  if (localCity) {
    const estimate = getDeliveryEstimate(settings, { state, city });
    return {
      amount: isFreeShipping ? 0 : localCity.fee,
      label: settings.localDeliveryLabel || 'Local Delivery',
      description: settings.localDeliveryDescription || '',
      minDays: estimate ? estimate.minDays : localCity.minDays,
      maxDays: estimate ? estimate.maxDays : localCity.maxDays,
      isLocalDelivery: true,
      isFreeShipping,
      codAvailable,
      localCityNote: localCity.note || '',
      localCityCutoff: localCity.cutoffTime || '',
    };
  }

  // 2. Shipping zone match
  const zone = matchZone(settings.zones, state);
  if (zone) {
    const rate = pickCheapestRate(zone.rates);
    if (rate) {
      const estimate = getDeliveryEstimate(settings, { state, city });
      return {
        amount: isFreeShipping ? 0 : rate.price,
        label: rate.label,
        description: rate.description || '',
        minDays: estimate ? estimate.minDays : rate.minDays,
        maxDays: estimate ? estimate.maxDays : rate.maxDays,
        isLocalDelivery: false,
        isFreeShipping,
        codAvailable,
      };
    }
  }

  // 3. Flat-rate fallback
  if (settings.flatRateEnabled) {
    const estimate = getDeliveryEstimate(settings, { state, city });
    return {
      amount: isFreeShipping ? 0 : settings.flatRatePrice,
      label: settings.flatRateLabel || 'Standard Shipping',
      description: settings.flatRateDescription || '',
      minDays: estimate ? estimate.minDays : settings.flatRateMinDays,
      maxDays: estimate ? estimate.maxDays : settings.flatRateMaxDays,
      isLocalDelivery: false,
      isFreeShipping,
      codAvailable,
    };
  }

  return { amount: 0, label: 'Shipping', minDays: null, maxDays: null, isLocalDelivery: false, isFreeShipping, codAvailable };
}

/**
 * Is COD eligible for this order total + location?
 * Returns { eligible, reason }.
 */
export function codEligibility(settings, subtotal, { state, city, pincode } = {}) {
  if (!settings) return { eligible: true, reason: '' };
  if (!settings.codEnabled) return { eligible: false, reason: 'Cash on Delivery is currently unavailable.' };
  if (settings.codMinOrder > 0 && subtotal < settings.codMinOrder) {
    return { eligible: false, reason: `Minimum order ₹${settings.codMinOrder} required for Cash on Delivery.` };
  }
  if (settings.codMaxOrder > 0 && subtotal > settings.codMaxOrder) {
    return { eligible: false, reason: `Cash on Delivery is not available for orders above ₹${settings.codMaxOrder}.` };
  }
  // Only apply region restrictions once the user has entered a state —
  // before that, default to eligible so COD isn't disabled on page load.
  if (state && !isCodAvailable(settings, { state, city, pincode })) {
    return { eligible: false, reason: 'Cash on Delivery is not available for your delivery location.' };
  }
  return { eligible: true, reason: '' };
}